import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { SharetribeApi } from '../../util/api';
import { ListingState, UserType } from '../../util/enums';
import { showErrorToast, showSuccessToast } from '../../util/toast';
import { syncTalentProfileOwnList } from '../LinkTalentPage/LinkTalentPage.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_OWN_LISTING_REQUEST = 'app/BulkUploadUsersPage/FETCH_OWN_LISTING_REQUEST';
export const FETCH_OWN_LISTING_SUCCESS = 'app/BulkUploadUsersPage/FETCH_OWN_LISTING_SUCCESS';
export const FETCH_OWN_LISTING_ERROR = 'app/BulkUploadUsersPage/FETCH_OWN_LISTING_ERROR';

export const CREATE_USER_PROFILE_REQUEST = 'app/BulkUploadUsersPage/CREATE_USER_PROFILE_REQUEST';
export const CREATE_USER_PROFILE_SUCCESS = 'app/BulkUploadUsersPage/CREATE_USER_PROFILE_SUCCESS';
export const CREATE_USER_PROFILE_ERROR = 'app/BulkUploadUsersPage/CREATE_USER_PROFILE_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchListingInProgress: false,
  fetchListingError: null,
  listingData: null,
  createUserProfileInProgress: false,
  createUserProfileError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_OWN_LISTING_REQUEST:
      return { ...state, fetchListingInProgress: true, fetchListingError: null };
    case FETCH_OWN_LISTING_SUCCESS:
      return { ...state, fetchListingInProgress: false, listingData: payload };
    case FETCH_OWN_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };

    case CREATE_USER_PROFILE_REQUEST:
      return { ...state, createUserProfileInProgress: true, createUserProfileError: null };
    case CREATE_USER_PROFILE_SUCCESS:
      return { ...state, createUserProfileInProgress: false, createUserProfileError: null };
    case CREATE_USER_PROFILE_ERROR:
      return { ...state, createUserProfileInProgress: false, createUserProfileError: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //
export const createUserProfileInProgressSelector = state =>
  state.BulkUploadUsersPage.createUserProfileInProgress;

// ================ Action creators ================ //

export const fetchOwnListingRequest = () => ({ type: FETCH_OWN_LISTING_REQUEST });
export const fetchOwnListingSuccess = payload => ({ type: FETCH_OWN_LISTING_SUCCESS, payload });
export const fetchOwnListingError = payload => ({
  type: FETCH_OWN_LISTING_ERROR,
  payload,
  error: true,
});

export const createUserProfileRequest = () => ({ type: CREATE_USER_PROFILE_REQUEST });
export const createUserProfileSuccess = () => ({ type: CREATE_USER_PROFILE_SUCCESS });
export const createUserProfileError = payload => ({
  type: CREATE_USER_PROFILE_ERROR,
  payload,
  error: true,
});

// ================ Thunks ================ //

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

export const requestFetchOwnListing = (id, config) => (dispatch, getState, sdk) => {
  dispatch(fetchOwnListingRequest());

  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
  const queryParams = {
    include: ['author', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
  };

  return sdk.ownListings
    .show({ id: new UUID(id), ...queryParams })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOwnListingSuccess({ id: response.data.data }));
      return response;
    })
    .catch(e => {
      log.error(e, 'fetch-own-listing-failed', { id });
      dispatch(fetchOwnListingError(storableError(e)));
      throw e;
    });
};

// Helper function to create a delay
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to create a single user and listing
const createUserAndListing = async ({
  user,
  listingData,
  sdk,
  dispatch,
  parentListingId,
  userType,
  agencyName,
  parentListingTitle,
}) => {
  try {
    // Create user
    const userResponse = await sdk.currentUser.create({
      email: user.email,
      password: 'Abc@123456',
      firstName: user.firstName,
      lastName: user.lastName,
      displayName: user.displayName,
      publicData: {
        userType: UserType.TALENT,
        isClaimable: true,
      },
    });

    const userId = userResponse?.data?.data?.id?.uuid;
    if (!userId) {
      throw new Error(`No user ID returned for email: ${user.email}`);
    }

    const agenyPayload = {
      pub_agency: {
        name: agencyName?.trim(),
        id: parentListingId,
      },
      agency_name: agencyName?.trim(),
    };

    const schoolPayload = {
      associatedGroup: {
        pub_school_or_group_name: parentListingTitle?.trim(),
        id: parentListingId,
      },
      pub_school_or_group_name: parentListingTitle?.trim(),
    };
    // Prepare updated listing data
    const updatedListingData = {
      ...listingData,
      title: `${user.firstName} ${user.lastName}`,
      authorId: userId,
      state: ListingState.PUBLISHED,
      publicData: {
        ...listingData?.publicData,
        listingCategory: UserType.TALENT,
        isClaimable: true,
        ...(userType === UserType.AGENCY && agenyPayload),
        ...(userType === UserType.SCHOOL && schoolPayload),
      },
    };

    // Call createListing function
    const listingResponse = await SharetribeApi.createListing(updatedListingData);

    const listingId = listingResponse?.data?.listingId;

    if (!listingId) {
      throw new Error(`No listing ID returned for user: ${userId}`);
    }

    // Return user data for batch sync
    return {
      userId,
      listingId,
      email: user.email,
      talentData: {
        talentId: userId,
        value: user?.email,
      },
    };
  } catch (error) {
    return { error, email: user.email };
  }
};

export const createUserProfile = params => async (dispatch, getState, sdk) => {
  dispatch(createUserProfileRequest());

  const {
    users = [],
    listingData = {},
    parentListingId,
    userType,
    agencyName,
    parentListingTitle,
  } = params || {};

  if (!users.length) {
    const error = new Error('No users provided');
    dispatch(createUserProfileError(storableError(error)));
    return;
  }

  try {
    const batchSize = 5; // Process 5 users at a time (30/min limit)
    const delayMs = 2000; // 2 seconds delay between batches
    const results = [];
    const associatedUsersArray = []; // Collect all talent data

    // Process users in batches
    for (let i = 0; i < users.length; i += batchSize) {
      const batch = users.slice(i, i + batchSize);
      const batchPromises = batch.map(user =>
        createUserAndListing({
          user,
          listingData,
          sdk,
          dispatch,
          parentListingId,
          userType,
          agencyName,
          parentListingTitle,
        })
      );
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Collect successful talent data
      batchResults.forEach(result => {
        if (result.talentData && result.listingId && !result.error) {
          associatedUsersArray.push(result.talentData);
        }
      });

      // Delay between batches (except for the last batch)
      if (i + batchSize < users.length) {
        await delay(delayMs);
      }
    }

    // Sync all users at once after all batches complete
    if (associatedUsersArray.length > 0 && parentListingId) {
      dispatch(
        syncTalentProfileOwnList({
          associatedUsers: associatedUsersArray,
          listingId: parentListingId,
          associatedListingCategory: userType,
        })
      );
    }

    showSuccessToast('Successfully created users profile');
    dispatch(createUserProfileSuccess());
  } catch (error) {
    showErrorToast('Failed to create users profile');
    dispatch(createUserProfileError(storableError(error)));
  }
};
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { id } = params;
  return dispatch(requestFetchOwnListing(id, config));
};
